import { useController } from "react-hook-form";

import { cn } from "@/lib/utils";

import ToggleButton from "../../inputs/ToggleButton/ToggleButton";
import { useFieldIds } from "../../useFieldIds";
import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

type ToggleButtonFieldProps = {
  readonly name: string;
  readonly label?: string;
  readonly hint?: string;
  readonly disabled?: boolean;
  readonly trueLabel?: string;
  readonly falseLabel?: string;
  readonly className?: string;
  readonly labelClassName?: string;
  readonly variant?: "default" | "v3";
};

export default function ToggleButtonField({
  name,
  label,
  hint,
  disabled = false,
  trueLabel = "Yes",
  falseLabel = "No",
  className,
  labelClassName,
  variant = "default",
}: ToggleButtonFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId, describedBy } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
  });

  const invalid = !!fieldState.error;

  return (
    <div
      className={cn("ToggleButtonField", "grid gap-2")}
      data-testid="ToggleButtonField"
    >
      <div className="flex items-center gap-2">
        {label && (
          <FieldLabel
            className={labelClassName}
            htmlFor={inputId}
            id={labelId}
            invalid={invalid}
            value={label}
          />
        )}

        <ToggleButton
          aria-describedby={describedBy(!!hint, !!fieldState.error)}
          aria-invalid={invalid}
          className={className}
          disabled={disabled}
          falseLabel={falseLabel}
          id={inputId}
          trueLabel={trueLabel}
          value={field.value}
          variant={variant}
          onChange={field.onChange}
        />
      </div>

      <div className={cn("align-center flex h-4 justify-end")}>
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
